import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Article, ArticleMasseApercu, ArticleMasseApercuLigne, ArticleMasseFamilleLigne,
  ArticleMasseFamilleParams, ArticleMassePartageLigne, ArticleMassePartageParams, ArticleMassePrixParams,
  ArticleMasseValeursLigne, ArticleMasseValeursParams,
  ArticleMasseFournisseurLigne, ArticleMasseFournisseurParams,
} from '../models/models';
import api from './api';
import { labels } from '../constants/labels';
import { partageToParams } from '../utils/partageFiltre';

/**
 * Actions de masse par famille (migration 0134) — état isolé de la liste des
 * articles (ses propres loading / messages) : l'aperçu et l'application sont
 * calculés côté serveur (ArticleService.apercuPrixMasse / appliquerPrixMasse).
 */
interface ArticleMasseState {
  apercuData: ArticleMasseApercuLigne[];          // action « ajuster les prix »
  apercuFamilleData: ArticleMasseFamilleLigne[];  // action « changer de famille »
  apercuPartageData: ArticleMassePartageLigne[];  // action « partage par département »
  apercuValeursData: ArticleMasseValeursLigne[];  // action « valeurs de la famille »
  apercuFournisseurData: ArticleMasseFournisseurLigne[];  // action « affecter un fournisseur » (0143)
  total: number;
  avertissementCmup: boolean;
  loading: boolean;
  applying: boolean;
  error: string | null;
  successMessage: string | null;
}

interface ArticleState {
  articleData: Article[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedArticle: Article | null;
  masse: ArticleMasseState;
}

const initialMasse: ArticleMasseState = {
  apercuData: [],
  apercuFamilleData: [],
  apercuPartageData: [],
  apercuValeursData: [],
  apercuFournisseurData: [],
  total: 0,
  avertissementCmup: false,
  loading: false,
  applying: false,
  error: null,
  successMessage: null,
};

const initialState: ArticleState = {
  articleData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedArticle: null,
  masse: initialMasse,
};

// 🔄 Fetch articles avec filtres optionnels
export const fetchArticleByFilters = createAsyncThunk<
  { data: Article[]; total: number; totalPages: number; currentPage: number },
  { code?: string|null; designation?: string|null; famille_id?: number|null; fournisseur_id?: number|null; visible?: 0|1|null; departement_id?: number|null; partage?: number[]|null; limit: number; offset: number },
  { rejectValue: string }
>(
  'article/fetchArticleByFilters',
  async ({ code = null, designation = null, famille_id = null, fournisseur_id = null, visible = null, departement_id, partage, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/articles/getByFilters', {
        // partage : filtre « Partage » (0132) → partage_tous / partage_departements
        // fournisseur_id (0143) : null = pas de filtre (omis par axios), 0 = sans fournisseur
        params: { code, designation, famille_id, fournisseur_id, visible, ...(departement_id != null && { departement_id }), ...partageToParams(partage), limit, offset },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// ✍️ Create or update article
export const createArticle = createAsyncThunk<Article, Article, { rejectValue: string }>(
  'article/createOrUpdate',
  async (articleData, thunkAPI) => {
    try {
      console.log(api.defaults.baseURL + '/articles/createOrUpdate', articleData);  
      const response = await api.post('/articles/createOrUpdate', articleData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// ❌ Delete article
export const importArticlesExcel = createAsyncThunk<
  { success: number; errors: Array<{ row: number; message: string }> },
  { lignes: Record<string, any>[]; completer_famille?: boolean },   // completer_famille : cellules vides ← famille (0136)
  { rejectValue: string }
>('articles/importExcel', async (payload, thunkAPI) => {
  try {
    const response = await api.post('/articles/import', payload);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l\'import'
    );
  }
});

export const deleteArticle = createAsyncThunk<number, number, { rejectValue: string }>(
  'article/deleteArticle',
  async (articleId, thunkAPI) => {
    try {
      await api.delete('/articles', { params: { id: articleId } });
      return articleId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

// ── Actions de masse par famille (migration 0134) ─────────────────────────────

/** POST /articles/masse/prix/apercu — aperçu paginé avant → après. */
export const fetchApercuPrixMasse = createAsyncThunk<ArticleMasseApercu, ArticleMassePrixParams, { rejectValue: string }>(
  'article/apercuPrixMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/prix/apercu', params, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec du calcul de l'aperçu.");
    }
  }
);

/** POST /articles/masse/prix/appliquer — applique l'ajustement (articles exclus retirés). */
export const appliquerPrixMasse = createAsyncThunk<{ nb_articles: number }, ArticleMassePrixParams, { rejectValue: string }>(
  'article/appliquerPrixMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/prix/appliquer', params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'application.");
    }
  }
);

/** POST /articles/masse/famille/apercu — aperçu paginé du changement de famille. */
export const fetchApercuFamilleMasse = createAsyncThunk<
  ArticleMasseApercu<ArticleMasseFamilleLigne>, ArticleMasseFamilleParams, { rejectValue: string }
>(
  'article/apercuFamilleMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/famille/apercu', params, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec du calcul de l'aperçu.");
    }
  }
);

/** POST /articles/masse/famille/appliquer — change la famille (articles exclus retirés). */
export const appliquerFamilleMasse = createAsyncThunk<{ nb_articles: number }, ArticleMasseFamilleParams, { rejectValue: string }>(
  'article/appliquerFamilleMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/famille/appliquer', params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'application.");
    }
  }
);

/** POST /articles/masse/fournisseur/apercu — aperçu paginé de l'affectation de fournisseur (0143). */
export const fetchApercuFournisseurMasse = createAsyncThunk<
  ArticleMasseApercu<ArticleMasseFournisseurLigne>, ArticleMasseFournisseurParams, { rejectValue: string }
>(
  'article/apercuFournisseurMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/fournisseur/apercu', params, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec du calcul de l'aperçu.");
    }
  }
);

/** POST /articles/masse/fournisseur/appliquer — affecte le fournisseur (articles exclus retirés). */
export const appliquerFournisseurMasse = createAsyncThunk<{ nb_articles: number }, ArticleMasseFournisseurParams, { rejectValue: string }>(
  'article/appliquerFournisseurMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/fournisseur/appliquer', params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'application.");
    }
  }
);

/** POST /articles/masse/partage/apercu — aperçu paginé du partage par département. */
export const fetchApercuPartageMasse = createAsyncThunk<
  ArticleMasseApercu<ArticleMassePartageLigne>, ArticleMassePartageParams, { rejectValue: string }
>(
  'article/apercuPartageMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/partage/apercu', params, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec du calcul de l'aperçu.");
    }
  }
);

/** POST /articles/masse/partage/appliquer — modifie le partage (articles exclus retirés). */
export const appliquerPartageMasse = createAsyncThunk<{ nb_articles: number }, ArticleMassePartageParams, { rejectValue: string }>(
  'article/appliquerPartageMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/partage/appliquer', params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'application.");
    }
  }
);

/** POST /articles/masse/valeurs/apercu — aperçu des valeurs de la famille appliquées. */
export const fetchApercuValeursMasse = createAsyncThunk<
  ArticleMasseApercu<ArticleMasseValeursLigne>, ArticleMasseValeursParams, { rejectValue: string }
>(
  'article/apercuValeursMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/valeurs/apercu', params, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec du calcul de l'aperçu.");
    }
  }
);

/** POST /articles/masse/valeurs/appliquer — applique les valeurs de la famille (exclus retirés). */
export const appliquerValeursMasse = createAsyncThunk<{ nb_articles: number }, ArticleMasseValeursParams, { rejectValue: string }>(
  'article/appliquerValeursMasse',
  async (params, thunkAPI) => {
    try {
      const response = await api.post('/articles/masse/valeurs/appliquer', params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'application.");
    }
  }
);

const articleSlice = createSlice({
  name: 'article',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedArticle: (state, action: PayloadAction<Article | null>) => {
      state.selectedArticle = action.payload;
    },
    clearArticleData: (state) => {
      state.articleData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearApercuMasse: (state) => {
      state.masse.apercuData = [];
      state.masse.apercuFamilleData = [];
      state.masse.apercuPartageData = [];
      state.masse.apercuValeursData = [];
      state.masse.apercuFournisseurData = [];
      state.masse.total = 0;
      state.masse.avertissementCmup = false;
    },
    clearMessagesMasse: (state) => {
      state.masse.error = null;
      state.masse.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticleByFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArticleByFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.articleData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchArticleByFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createArticle.fulfilled, (state, action: PayloadAction<Article>) => {
        state.loading = false;
        state.successMessage = 'Création/Mise à jour réussie !';
      })
      .addCase(createArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteArticle.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Suppression réussie !';
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // ── Actions de masse ──
      .addCase(fetchApercuPrixMasse.pending, (state) => {
        state.masse.loading = true;
        state.masse.error = null;
      })
      .addCase(fetchApercuPrixMasse.fulfilled, (state, action) => {
        state.masse.loading = false;
        state.masse.apercuData = action.payload.data;
        state.masse.total = action.payload.total;
        state.masse.avertissementCmup = action.payload.avertissement_cmup;
      })
      .addCase(fetchApercuPrixMasse.rejected, (state, action) => {
        if (action.meta.aborted) return;   // aperçu remplacé par un plus récent
        state.masse.loading = false;
        state.masse.error = action.payload as string;
      })
      .addCase(appliquerPrixMasse.pending, (state) => {
        state.masse.applying = true;
        state.masse.error = null;
        state.masse.successMessage = null;
      })
      .addCase(appliquerPrixMasse.fulfilled, (state, action) => {
        state.masse.applying = false;
        state.masse.successMessage = `${action.payload.nb_articles} article(s) mis à jour.`;
      })
      .addCase(appliquerPrixMasse.rejected, (state, action) => {
        state.masse.applying = false;
        state.masse.error = action.payload as string;
      })
      .addCase(fetchApercuFamilleMasse.pending, (state) => {
        state.masse.loading = true;
        state.masse.error = null;
      })
      .addCase(fetchApercuFamilleMasse.fulfilled, (state, action) => {
        state.masse.loading = false;
        state.masse.apercuFamilleData = action.payload.data;
        state.masse.total = action.payload.total;
        state.masse.avertissementCmup = false;
      })
      .addCase(fetchApercuFamilleMasse.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.masse.loading = false;
        state.masse.error = action.payload as string;
      })
      .addCase(appliquerFamilleMasse.pending, (state) => {
        state.masse.applying = true;
        state.masse.error = null;
        state.masse.successMessage = null;
      })
      .addCase(appliquerFamilleMasse.fulfilled, (state, action) => {
        state.masse.applying = false;
        state.masse.successMessage = `${action.payload.nb_articles} article(s) changé(s) de famille.`;
      })
      .addCase(appliquerFamilleMasse.rejected, (state, action) => {
        state.masse.applying = false;
        state.masse.error = action.payload as string;
      })
      .addCase(fetchApercuFournisseurMasse.pending, (state) => {
        state.masse.loading = true;
        state.masse.error = null;
      })
      .addCase(fetchApercuFournisseurMasse.fulfilled, (state, action) => {
        state.masse.loading = false;
        state.masse.apercuFournisseurData = action.payload.data;
        state.masse.total = action.payload.total;
        state.masse.avertissementCmup = false;
      })
      .addCase(fetchApercuFournisseurMasse.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.masse.loading = false;
        state.masse.error = action.payload as string;
      })
      .addCase(appliquerFournisseurMasse.pending, (state) => {
        state.masse.applying = true;
        state.masse.error = null;
        state.masse.successMessage = null;
      })
      .addCase(appliquerFournisseurMasse.fulfilled, (state, action) => {
        state.masse.applying = false;
        state.masse.successMessage = `${action.payload.nb_articles} article(s) : ${labels.fournisseurMisAJour}.`;
      })
      .addCase(appliquerFournisseurMasse.rejected, (state, action) => {
        state.masse.applying = false;
        state.masse.error = action.payload as string;
      })
      .addCase(fetchApercuPartageMasse.pending, (state) => {
        state.masse.loading = true;
        state.masse.error = null;
      })
      .addCase(fetchApercuPartageMasse.fulfilled, (state, action) => {
        state.masse.loading = false;
        state.masse.apercuPartageData = action.payload.data;
        state.masse.total = action.payload.total;
        state.masse.avertissementCmup = false;
      })
      .addCase(fetchApercuPartageMasse.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.masse.loading = false;
        state.masse.error = action.payload as string;
      })
      .addCase(appliquerPartageMasse.pending, (state) => {
        state.masse.applying = true;
        state.masse.error = null;
        state.masse.successMessage = null;
      })
      .addCase(appliquerPartageMasse.fulfilled, (state, action) => {
        state.masse.applying = false;
        state.masse.successMessage = `${action.payload.nb_articles} article(s) : partage mis à jour.`;
      })
      .addCase(appliquerPartageMasse.rejected, (state, action) => {
        state.masse.applying = false;
        state.masse.error = action.payload as string;
      })
      .addCase(fetchApercuValeursMasse.pending, (state) => {
        state.masse.loading = true;
        state.masse.error = null;
      })
      .addCase(fetchApercuValeursMasse.fulfilled, (state, action) => {
        state.masse.loading = false;
        state.masse.apercuValeursData = action.payload.data;
        state.masse.total = action.payload.total;
        state.masse.avertissementCmup = action.payload.avertissement_cmup;
      })
      .addCase(fetchApercuValeursMasse.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.masse.loading = false;
        state.masse.error = action.payload as string;
      })
      .addCase(appliquerValeursMasse.pending, (state) => {
        state.masse.applying = true;
        state.masse.error = null;
        state.masse.successMessage = null;
      })
      .addCase(appliquerValeursMasse.fulfilled, (state, action) => {
        state.masse.applying = false;
        state.masse.successMessage = `${action.payload.nb_articles} article(s) mis à jour avec les valeurs de la famille.`;
      })
      .addCase(appliquerValeursMasse.rejected, (state, action) => {
        state.masse.applying = false;
        state.masse.error = action.payload as string;
      });
  },
});

export const {
  setCurrentPage, setSelectedArticle, clearArticleData, clearMessages,
  clearApercuMasse, clearMessagesMasse,
} = articleSlice.actions;
export default articleSlice.reducer;
